import { API } from "@swivl-mono/api-helper";
import { weeklyPeriodsOverlap } from "@swivl-mono/helpers";
import { ScheduleExceptionInterface, ScheduleInterface, WeeklyPeriod, defaultWeeklyForSlug, isCustomScheduleSlug } from "@swivl-mono/types";
import { Button, ButtonSize, ButtonVariant, Tooltip } from "@swivl-mono/ui";
import { ReactNode, useEffect, useState } from "react";
import { GrRevert } from "react-icons/gr";

import { Member } from "../../../../../Models/Member/Member.Current";
import { ExceptionList } from "./ExceptionList";
import { InheritancePill } from "./InheritancePill";
import { CardEditAction, CardSaveActions, ScheduleCard } from "./ScheduleCard";
import { PageScope, pickEffectiveSchedule, rowScopeKind } from "./Scope";
import { WeeklyGrid, WeeklySummary } from "./WeeklyGrid";
import { copyMondayToWeekdays, hasMondayHours } from "./scheduleTime";

interface ScheduleSlugSectionProps {
  /** Custom group at the company level: rename and remove the whole group (ENG-3216 decision 50). */
  onRenameGroup?: (label: string) => Promise<void>;
  onDeleteGroup?: () => Promise<void>;
  slug: string;
  label: string;
  caption: ReactNode;
  scope: PageScope;
}

/**
 * Per-slug schedule editor for one of the three scopes (org / group / location).
 * Shows the row that wins the cascade at the current scope, as a read-only
 * weekly summary until the operator clicks Edit.
 *
 * Editing an inherited schedule needs no separate "Override" step — the grid is
 * seeded from whatever is inherited, and saving writes a row at this scope,
 * which is what an override is. Reverting deletes this scope's row so the
 * cascade resolves again.
 *
 * Exceptions are authored per scope; resolution walks scopes most-specific
 * first, so a location can supersede the org's "Closed Aug 5".
 */
export function ScheduleSlugSection({
  slug,
  label,
  caption,
  scope,
  onRenameGroup,
  onDeleteGroup
}: ScheduleSlugSectionProps): React.ReactElement | null {
  const orgId = Member.orgId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allRows, setAllRows] = useState<ScheduleInterface[]>([]);
  const [exceptions, setExceptions] = useState<ScheduleExceptionInterface[]>([]);
  const [draftPeriods, setDraftPeriods] = useState<WeeklyPeriod[] | null>(null);
  // Date-override edits ride along with the card's Save rather than writing on
  // their own, so one Save covers everything changed here.
  const [stagedExceptions, setStagedExceptions] = useState<Partial<ScheduleExceptionInterface>[]>([]);
  const [deletedExceptionIds, setDeletedExceptionIds] = useState<number[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async (): Promise<void> => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const [rows, exns] = await Promise.all([
        API.Schedule.listAllSchedulesForOrg(orgId),
        API.Schedule.listExceptionsAtScope(orgId, { groupId: scope.groupId, locationId: scope.locationId }, slug)
      ]);
      setAllRows(rows);
      setExceptions(exns);
      setDraftPeriods(null);
      setStagedExceptions([]);
      setDeletedExceptionIds([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, scope.kind, scope.groupId, scope.locationId, slug]);

  if (!orgId) return null;

  const effective = pickEffectiveSchedule(allRows, scope, slug);
  const effectiveScope = effective ? rowScopeKind(effective) : undefined;
  const ownsHere = effectiveScope === scope.kind;
  // No row at any scope: the group's built-in default hours are what every consumer runs
  // on (ENG-3216 decision 49), so that is what the card shows and what Edit starts from.
  const defaultWeekly = defaultWeeklyForSlug(slug);
  const periods = draftPeriods ?? effective?.weekly ?? [...defaultWeekly];

  // What a revert would leave behind: the row that wins once this scope's row
  // is gone. Undefined means nothing above, so reverting clears the schedule.
  const parentRow =
    ownsHere && effective
      ? pickEffectiveSchedule(
          allRows.filter((r) => r.id !== effective.id),
          scope,
          slug
        )
      : null;

  const handleEdit = (): void => {
    // Seed from whatever is inherited — or the default — so editing starts from
    // today's hours rather than an empty week.
    setDraftPeriods(effective?.weekly ?? [...defaultWeekly]);
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setDraftPeriods(null);
    setStagedExceptions([]);
    setDeletedExceptionIds([]);
    setIsEditing(false);
  };

  const handleSave = async (): Promise<void> => {
    if (draftPeriods === null) return;
    setSaving(true);
    setError(null);
    try {
      // Weekly grid first: an override's row is scoped the same way, so writing
      // the schedule before its exceptions keeps the two consistent if a later
      // call fails.
      await API.Schedule.upsertSchedule(orgId, {
        groupId: scope.kind === "group" ? (scope.groupId ?? null) : null,
        locationId: scope.kind === "location" ? (scope.locationId ?? null) : null,
        slug,
        label: effective?.label ?? label,
        weekly: draftPeriods,
        isActive: true
      });
      for (const exception of stagedExceptions) {
        await API.Schedule.upsertException(orgId, exception);
      }
      for (const exceptionId of deletedExceptionIds) {
        await API.Schedule.deleteException(orgId, exceptionId);
      }
      await refresh();
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (): Promise<void> => {
    if (!effective || !ownsHere) return;
    const message = parentRow
      ? "Revert to Parent? This deletes this scope's hours and falls back to the inherited schedule."
      : "Revert to Default? This deletes these hours; the group's default hours apply here again.";
    if (!window.confirm(message)) return;
    setSaving(true);
    try {
      await API.Schedule.deleteSchedule(orgId, effective.id);
      await refresh();
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRenameGroup = async (): Promise<void> => {
    if (!onRenameGroup) return;
    const next = window.prompt("Rename this schedule group", label);
    if (next === null || !next.trim() || next.trim() === label) return;
    setSaving(true);
    try {
      await onRenameGroup(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (): Promise<void> => {
    if (!onDeleteGroup) return;
    if (!window.confirm(`Remove "${label}"? This deletes its hours at every group and location and its date overrides.`)) return;
    setSaving(true);
    try {
      await onDeleteGroup();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  const revertLabel = parentRow ? "Revert to Parent" : "Revert to Default";
  // Names the scope rather than spelling out the week — seven days of hours
  // makes for an unreadable tooltip.
  const revertsTo = parentRow ? `the ${rowScopeKind(parentRow) === "group" ? "group's" : "org's"} schedule` : "the default hours";

  // Reverting is meaningful for any row owned here, the company's included: every group
  // has default hours beneath it (ENG-3216 decision 56), so nothing is ever left empty.
  // The one exception is a custom group's company row — that row IS the group (its name
  // lives on it), so the card offers "Remove group" there instead of a revert.
  const canRevert = ownsHere && !!effective && (parentRow !== null || !isCustomScheduleSlug(slug));

  return (
    <ScheduleCard
      title={label}
      caption={caption}
      status={<InheritancePill source={effectiveScope} currentScope={scope.kind} fallsBackToDefault />}
      error={error}
      loading={loading}
      actions={
        isEditing ? (
          <>
            {canRevert && (
              <Tooltip content={`Reverts to ${revertsTo}`}>
                <button
                  type="button"
                  onClick={handleRevert}
                  disabled={saving}
                  className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
                >
                  {revertLabel}
                  <GrRevert className="size-4" />
                </button>
              </Tooltip>
            )}
            <Button
              variant={ButtonVariant.GHOST}
              size={ButtonSize.SMALL}
              disabled={saving || !hasMondayHours(periods)}
              onClick={() => setDraftPeriods(copyMondayToWeekdays(periods))}
            >
              Copy Monday to all weekdays
            </Button>
            <CardSaveActions saving={saving} canSave={!weeklyPeriodsOverlap(periods)} onCancel={handleCancel} onSave={handleSave} />
          </>
        ) : (
          <>
            {onRenameGroup && (
              <Button variant={ButtonVariant.GHOST} size={ButtonSize.SMALL} disabled={saving} onClick={() => void handleRenameGroup()}>
                Rename
              </Button>
            )}
            {onDeleteGroup && (
              <Button variant={ButtonVariant.GHOST} size={ButtonSize.SMALL} disabled={saving} onClick={() => void handleDeleteGroup()}>
                Remove group
              </Button>
            )}
            <CardEditAction onEdit={handleEdit} />
          </>
        )
      }
    >
      {isEditing ? <WeeklyGrid periods={periods} onChange={setDraftPeriods} /> : <WeeklySummary periods={periods} />}

      <ExceptionList
        scope={scope}
        slug={slug}
        exceptions={exceptions}
        staged={stagedExceptions}
        deletedIds={deletedExceptionIds}
        isEditing={isEditing}
        onStage={(exception) => setStagedExceptions((prev) => [...prev, exception])}
        onUnstage={(index) => setStagedExceptions((prev) => prev.filter((_, i) => i !== index))}
        onDelete={(exceptionId) => setDeletedExceptionIds((prev) => [...prev, exceptionId])}
      />
    </ScheduleCard>
  );
}
